import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService, SessionService } from '../../../shared/services';
import { ApiResponseObject, TicketStatus } from './models';

@Injectable({
  providedIn: 'root',
})
export class TicketsService {
  constructor(
    private api: ApiService,
    private session: SessionService
  ) {}

  private tickets$: BehaviorSubject<any> = new BehaviorSubject([]);

  public getTicketsObs() {
    return this.tickets$.asObservable();
  }

  public updateTickets(data: any[]) {
    return this.tickets$.next(data);
  }

  public getTicketStatus() {
    return this.api
      .get('/v1/tickets/count', {
        queryParams: { bankId: this.session.userBank },
      })
      .pipe(
        map((res: any) => {
          if (res.statusCode === '00') {
            return res.responseObject;
          } else {
            return of([]);
          }
        }),
        catchError(err => throwError(err))
      );
  }

  public getTicket(id: string) {
    return this.api
      .get(`/v1/tickets/${id}`, {
        queryParams: { bankId: this.session.userBank },
      })
      .pipe(map((res: any) => res.responseObject));
  }

  public fetchAllTickets(pageIndex: number, pageSize: number, filters: any) {
    const domain = 'swift';
    let params = new HttpParams();
    let filterKeys = Object.keys(filters);
    filterKeys.forEach(key => {
      if (!filters[key] || (key === 'status' && filters[key] === 'All')) return;
      params = params.set(key, filters[key]);
    });
    return this.api
      .get(`/v2/tickets/paged/?page=${pageIndex}&take=${pageSize}&domain=${domain}&` + params.toString(), {
        queryParams: { bankId: this.session.userBank },
      })
      .pipe(
        map((res: any) => {
          if (res.statusCode === '00') {
            const items = res.responseObject.items.map((ticket: any, ind: any) => {
              let subject = ticket.subject;
              let status = ticket.status;
              let cssStatusClass = 'pending';
              let actionCssClass = '';
              let isCNP = subject == 'Access profile of a cutomer who is not present';
              if (status === 'Submitted' && isCNP) {
                actionCssClass = 'hover-underline cursor-pointer';
              }
              if (!['Completed', 'New', 'Rejected'].includes(status) || (status === 'Completed' && isCNP)) {
                actionCssClass = 'hover-underline cursor-pointer';
              }
              if (status == 'New') {
                cssStatusClass = 'new';
              }
              if (['Aborted', 'Error', 'Rejected'].includes(status)) {
                cssStatusClass = 'error';
              }
              if (['Completed', 'Submitted'].includes(status)) {
                cssStatusClass = 'completed';
              }
              return {
                statusText: ticket.status.endsWith('NewGen') ? ticket.status : this.titleToPhrase(ticket.status),
                actionCssClass,
                cssStatusClass,
                ...ticket,
              };
            });
            return { items, itemsCount: res.responseObject.itemsCount };
          } else {
            return of([]);
          }
        })
      );
  }

  public getTicketsDropdown(): Observable<any> {
    return this.api
      .get<any>('/v1/tickets/dropdowns', {
        queryParams: { bankId: this.session.userBank },
      })
      .pipe(
        map((res: any) => {
          if (res.statusCode === '00') {
            return res.responseObject;
          } else {
            return of([]);
          }
        })
      );
  }

  public addTicketNote(id: any, data: any): Observable<any> {
    const url = `/v1/tickets/${id}/note`;
    return this.api.post<any>(url, data);
  }

  public updateTicketStatus(payload: TicketStatus): Observable<ApiResponseObject> {
    return this.api.post<any>(`/v1/backoffice/update`, payload);
  }

  private titleToPhrase(word: string) {
    let chars = word.split('');
    let spaced = chars.map((char, index) => {
      if (char === char.toUpperCase() && index > 0) {
        return ' ' + char;
      } else {
        return char;
      }
    });
    return spaced.join('');
  }
}
